import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import AirDatepicker from 'air-datepicker';
import { ChartType } from 'src/app/services/forbruk/forbruk.types';
import { CustomLocaleNb } from 'src/app/utils/date-utils';
import {
  addDays,
  addHours,
  addMonths,
  differenceInDays,
  differenceInMonths,
  isSameDay,
  isSameMonth,
  isSameYear
} from 'date-fns';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { ForbrukService } from 'src/app/services/forbruk/forbruk.service';
import { Subscription } from 'rxjs';
import { hasChanged } from 'src/app/utils/change-utils';
@Component({
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss']
})
export class DatepickerComponent implements OnInit {
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() selectedDateMonth: Date;
  @Input() selectedDateDay: Date;
  @Input() selectedDateHour: Date;
  @Input() startDate: Date;
  @Input() monthBack: boolean;
  @Input() monthForward: boolean;
  @Output() emitSelectedDateDay = new EventEmitter<Date>();
  @Output() emitSelectedDateHour = new EventEmitter<Date>();
  @Output() emitSelectedDateMonth = new EventEmitter<Date>();
  @Output() emitUpdateChart = new EventEmitter<void>();

  monthDayPicker: AirDatepicker<HTMLElement>;
  dayDatePicker: AirDatepicker<HTMLElement>;

  hourBack: boolean;
  hourForward: boolean;
  dayBack: boolean;
  dayForward: boolean;
  showMonthCal: boolean;
  showDayCal: boolean;
  _chartTypes: ChartType;
  currentYear: number;

  subscription: Subscription;
  constructor(
    private analyticsService: AnalyticsService,
    private _forbrukService: ForbrukService
  ) {
    this.monthBack = true;
    this.monthForward = false;
    this.dayBack = true;
    this.dayForward = false;
    this.hourBack = true;
    this.hourForward = false;
  }

  ngOnInit() {
    this.currentYear = new Date().getFullYear();
    this.subscription = this._forbrukService
      .getChartType()
      .subscribe((chartTypes) => {
        this._chartTypes = chartTypes;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes.minDate &&
      changes.maxDate &&
      this.monthDayPicker &&
      this.dayDatePicker
    ) {
      if (hasChanged([changes.minDate, changes.maxDate, changes.startDate])) {
        this.updateDatePickers();
      }
    }
  }

  ngOnDestory() {
    this.subscription?.unsubscribe();
  }

  ngAfterViewInit() {
    this.monthDayPicker = new AirDatepicker('#airDay', {
      startDate: this.startDate,
      locale: CustomLocaleNb,
      minDate: this.minDate,
      maxDate: this.maxDate,
      range: false,
      toggleSelected: false,
      view: 'months',
      minView: 'months',
      onSelect: function (date: Date) {
        if (date['formattedDate']) {
          this.setSelectedDate('month', date['date']);
          // Dobbelt opp til vi ønsker å fjerne andre browse events
          const diff = this.getTimeDifferenceInMonths(date['date']);
          // Dagsvisning: logge tidskontekst
          this.analyticsService.logEvent_viewItem(
            `${diff} måned tilbake`,
            'forbruk dag'
          );
        }
      }.bind(this)
    });
    if (this.maxDate < this.startDate) {
      this.monthDayPicker.selectDate(this.maxDate);
    } else if (this.minDate > this.startDate) {
      this.monthDayPicker.selectDate(this.minDate);
    } else {
      this.monthDayPicker.selectDate(this.startDate);
    }

    this.dayDatePicker = new AirDatepicker('#airHour', {
      startDate: this.startDate,
      locale: CustomLocaleNb,
      minDate: this.minDate,
      maxDate: this.maxDate,
      range: false,
      toggleSelected: false,
      view: 'days',
      minView: 'days',
      onSelect: function (date: Date) {
        if (date['formattedDate']) {
          this.setSelectedDate('day', date['date']);
          // Dobbelt opp til vi ønsker å fjerne andre browse events
          const diff = this.getTimeDifferenceInDays(date['date']);
          // Dagsvisning: logge tidskontekst
          this.analyticsService.logEvent_viewItem(
            `${diff} dag tilbake`,
            'forbruk time'
          );
        }
      }.bind(this)
    });
    if (this.maxDate < this.startDate) {
      this.dayDatePicker.selectDate(this.maxDate);
    } else if (this.minDate > this.startDate) {
      this.dayDatePicker.selectDate(this.minDate);
    } else {
      this.dayDatePicker.selectDate(this.startDate);
    }
  }

  setSelectedDate(chart: string, value: Date) {
    // måned
    if (chart == 'year') {
      if (!isSameYear(value, this.selectedDateMonth)) {
        this.selectedDateMonth = value;
        this.checkDateLimits(chart);
        this.emitSelectedDateMonth.emit(this.selectedDateMonth);
      }
    }

    // dag
    if (chart == 'month') {
      if (!isSameMonth(value, this.selectedDateDay)) {
        if (this.maxDate < value) {
          this.selectedDateDay = this.maxDate;
        } else if (value.getHours() == 0 || value.getHours() == 1) {
          this.selectedDateDay = addHours(value, 6);
        } else {
          this.selectedDateDay = value;
        }

        if (this.showMonthCal) {
          this.showMonthCal = false;
        }

        this.checkDateLimits(chart);
        this.emitSelectedDateDay.emit(this.selectedDateDay);
      }
    }

    // time
    if (chart == 'day') {
      if (!isSameDay(value, this.selectedDateHour)) {
        this.selectedDateHour = value;

        if (
          this.selectedDateHour.getHours() === 0 ||
          this.selectedDateHour.getHours() === 1
        ) {
          this.selectedDateHour = addHours(this.selectedDateHour, 6);
        }

        if (this.showDayCal) {
          this.showDayCal = false;
        }
        this.checkDateLimits(chart);
        this.emitSelectedDateHour.emit(this.selectedDateHour);
      }
    }
    if (
      (chart == 'year' && this._chartTypes.Maaned.active) ||
      (chart == 'month' && this._chartTypes.Dag.active) ||
      (chart == 'day' && this._chartTypes.Time.active)
    ) {
      this.emitUpdateChart.emit();
    }
  }

  checkDateLimits(chart: string) {
    // 0 = år, 1 = måned, 2 = dag, 3 = time
    const primMax = new Date(
      this.maxDate.getFullYear(),
      this.maxDate.getMonth(),
      this.maxDate.getDate(),
      0,
      0,
      0
    );
    const primMin = new Date(
      this.minDate.getFullYear(),
      this.minDate.getMonth(),
      this.minDate.getDate(),
      0,
      0,
      0
    );
    let primSel: Date;

    if (chart == 'year') {
      this.monthBack =
        this.selectedDateMonth.getFullYear() <= this.minDate.getFullYear()
          ? false
          : true;
      this.monthForward =
        this.selectedDateMonth.getFullYear() >= this.maxDate.getFullYear()
          ? false
          : true;
    }
    if (chart == 'month') {
      primSel = new Date(
        this.selectedDateDay.getFullYear(),
        this.selectedDateDay.getMonth(),
        1,
        0,
        0,
        0
      );
      const primMinMonth = new Date(
        this.minDate.getFullYear(),
        this.minDate.getMonth(),
        1,
        0,
        0,
        0
      );
      const primMaxMonth = new Date(
        this.maxDate.getFullYear(),
        this.maxDate.getMonth(),
        1,
        0,
        0,
        0
      );

      this.dayBack = primSel <= primMinMonth ? false : true;
      this.dayForward = primSel >= primMaxMonth ? false : true;
    }
    if (chart == 'day') {
      primSel = new Date(
        this.selectedDateHour.getFullYear(),
        this.selectedDateHour.getMonth(),
        this.selectedDateHour.getDate(),
        0,
        0,
        0
      );

      this.hourBack = primSel <= primMin ? false : true;
      this.hourForward = primSel >= primMax ? false : true;
    }
  }

  getTimeDifferenceInDays(date: Date): number {
    const today = new Date();
    return differenceInDays(today, date);
  }

  getTimeDifferenceInMonths(date: Date): number {
    const today = new Date();
    return differenceInMonths(today, date);
  }

  moveBack(chart: string) {
    if (chart == 'month') {
      this.checkDateLimits(chart);
      if (this.dayBack) {
        this.monthDayPicker.selectDate(addMonths(this.selectedDateDay, -1));
        this.checkDateLimits(chart);
      }
    }
    if (chart == 'day') {
      this.checkDateLimits(chart);
      if (this.hourBack) {
        this.dayDatePicker.selectDate(addDays(this.selectedDateHour, -1));
        this.checkDateLimits(chart);
      }
    }
  }

  moveForth(chart: string) {
    if (chart == 'month') {
      this.checkDateLimits(chart);
      if (this.dayForward) {
        this.monthDayPicker.selectDate(addMonths(this.selectedDateDay, 1));
        this.checkDateLimits(chart);
      }
    }
    if (chart == 'day') {
      this.checkDateLimits(chart);
      if (this.hourForward) {
        this.dayDatePicker.selectDate(addDays(this.selectedDateHour, 1));
        this.checkDateLimits(chart);
      }
    }
  }

  updateDatePickers() {
    this.monthDayPicker.clear();
    this.monthDayPicker.update({
      maxDate: this.maxDate,
      minDate: this.minDate
    });
    if (this.maxDate < this.selectedDateMonth) {
      this.monthDayPicker.selectDate(this.maxDate);
    } else if (this.minDate > this.selectedDateMonth) {
      this.monthDayPicker.selectDate(this.minDate);
    } else {
      this.monthDayPicker.selectDate(this.selectedDateMonth);
    }

    this.dayDatePicker.clear();
    this.dayDatePicker.update({
      maxDate: this.maxDate,
      minDate: this.minDate
    });
    if (this.maxDate < this.selectedDateDay) {
      this.dayDatePicker.selectDate(this.maxDate);
    } else if (this.minDate > this.selectedDateDay) {
      this.dayDatePicker.selectDate(this.minDate);
    } else {
      this.dayDatePicker.selectDate(this.selectedDateDay);
    }
  }

  showHideCal(chart: string) {
    if (chart == 'month') {
      this.showMonthCal = !this.showMonthCal;
    }
    if (chart == 'day') {
      this.showDayCal = !this.showDayCal;
    }
  }
}
